/**
 * External Services Catalog
 * Admin page showing all external services, AI providers, and third-party
 * integrations with usage metrics, cost tracking, and dependency classification.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { useTabbedChrome } from '@/components/weavein';
import {
  Brain,
  Database,
  Mic,
  MessageSquare,
  CreditCard,
  Cloud,
  Activity,
  Link2,
  FileText,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Search,
  Filter,
  RefreshCw,
  Loader2,
  DollarSign,
  Zap,
  Shield,
  Clock,
  Hash,
  TrendingUp,
  Info,
  BarChart3,
} from 'lucide-react';
import {
  fetchExternalServicesCatalog,
  ExternalServicesCatalogResponse,
  ExternalService,
  ServiceModel,
} from '@/services/api/externalServicesCatalog';

// ── Category configuration ───────────────────────────────────────────────

const CATEGORY_CONFIG: Record<string, { label: string; icon: React.ElementType; color: string }> = {
  ai_llm: { label: 'AI / LLM', icon: Brain, color: 'purple' },
  ai_embedding: { label: 'AI / Embeddings', icon: Database, color: 'indigo' },
  ai_transcription: { label: 'AI / Audio & Speech', icon: Mic, color: 'violet' },
  communication: { label: 'Communication', icon: MessageSquare, color: 'blue' },
  payment: { label: 'Payment & Financial', icon: CreditCard, color: 'green' },
  cloud: { label: 'Cloud Infrastructure', icon: Cloud, color: 'slate' },
  monitoring: { label: 'Monitoring', icon: Activity, color: 'orange' },
  integration: { label: 'Integration', icon: Link2, color: 'cyan' },
  data_source: { label: 'Data Sources', icon: FileText, color: 'amber' },
};

const DEPENDENCY_BADGE: Record<string, { label: string; className: string }> = {
  critical: { label: 'Critical', className: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300' },
  important: { label: 'Important', className: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300' },
  optional: { label: 'Optional', className: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300' },
};

const STATUS_INDICATOR: Record<string, { label: string; icon: React.ElementType; className: string }> = {
  active: { label: 'Active', icon: CheckCircle, className: 'text-green-600 dark:text-green-400' },
  configured: { label: 'Configured', icon: CheckCircle, className: 'text-blue-600 dark:text-blue-400' },
  not_configured: { label: 'Not Configured', icon: XCircle, className: 'text-gray-400 dark:text-gray-500' },
  deprecated: { label: 'Deprecated', icon: AlertTriangle, className: 'text-orange-500 dark:text-orange-400' },
};

const METERING_LABELS: Record<string, string> = {
  per_token: 'Per Token',
  per_message: 'Per Message',
  per_minute: 'Per Minute',
  per_api_call: 'Per API Call',
  per_seat: 'Per Seat',
  flat_rate: 'Flat Rate',
  free: 'Free',
};

// ── Helper components ────────────────────────────────────────────────────

function StatCard({ label, value, subValue, icon: Icon, color = 'blue' }: {
  label: string;
  value: string;
  subValue?: string;
  icon: React.ElementType;
  color?: string;
}) {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm text-gray-500 dark:text-gray-400">{label}</span>
        <Icon className={`h-5 w-5 text-${color}-500`} />
      </div>
      <div className="text-2xl font-bold text-gray-900 dark:text-white">{value}</div>
      {subValue && <div className="text-xs text-gray-400 dark:text-gray-500 mt-1">{subValue}</div>}
    </div>
  );
}

function ModelRow({ model }: { model: ServiceModel }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-2 py-2 px-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate">{model.display_name}</div>
        <div className="text-xs text-gray-500 dark:text-gray-400 font-mono">{model.model_id}</div>
      </div>
      <div className="flex flex-wrap gap-1">
        {model.use_cases.slice(0, 3).map((uc, i) => (
          <span key={i} className="text-[10px] px-1.5 py-0.5 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded-full whitespace-nowrap">{uc}</span>
        ))}
        {model.use_cases.length > 3 && (
          <span className="text-[10px] px-1.5 py-0.5 bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-300 rounded-full">+{model.use_cases.length - 3} more</span>
        )}
      </div>
      <div className="text-right text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
        {model.pricing_input_per_1m != null ? (
          <span>${model.pricing_input_per_1m}/{model.pricing_output_per_1m != null ? `$${model.pricing_output_per_1m}` : ''} per 1M</span>
        ) : model.pricing_notes ? (
          <span>{model.pricing_notes}</span>
        ) : null}
      </div>
    </div>
  );
}

// ── Service Card ─────────────────────────────────────────────────────────

function ServiceCard({ service, expanded, onToggle }: {
  service: ExternalService;
  expanded: boolean;
  onToggle: () => void;
}) {
  const catConfig = CATEGORY_CONFIG[service.category] || { label: service.category, icon: Zap, color: 'gray' };
  const CatIcon = catConfig.icon;
  const depBadge = DEPENDENCY_BADGE[service.dependency_level] || DEPENDENCY_BADGE.optional;
  const statusInfo = STATUS_INDICATOR[service.status] || STATUS_INDICATOR.active;
  const StatusIcon = statusInfo.icon;

  const hasUsage = service.usage.total_invocations > 0;

  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-sm overflow-hidden">
      {/* Header */}
      <button
        onClick={onToggle}
        className="w-full text-left px-5 py-4 flex items-center gap-4 hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors"
      >
        <div className={`flex-shrink-0 h-10 w-10 rounded-lg bg-${catConfig.color}-100 dark:bg-${catConfig.color}-900/30 flex items-center justify-center`}>
          <CatIcon className={`h-5 w-5 text-${catConfig.color}-600 dark:text-${catConfig.color}-400`} />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-gray-900 dark:text-white">{service.name}</span>
            <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${depBadge.className}`}>{depBadge.label}</span>
            {service.hipaa_eligible && (
              <span className="text-[10px] px-2 py-0.5 rounded-full font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300 flex items-center gap-0.5">
                <Shield className="h-3 w-3" />HIPAA
              </span>
            )}
            <StatusIcon className={`h-4 w-4 ${statusInfo.className}`} title={statusInfo.label} />
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 truncate">{service.provider} &middot; {catConfig.label} &middot; {METERING_LABELS[service.metering_type] || service.metering_type}</div>
        </div>

        {/* Quick stats */}
        <div className="hidden md:flex items-center gap-6 text-xs text-gray-500 dark:text-gray-400">
          {hasUsage && (
            <>
              <div className="text-right">
                <div className="font-semibold text-gray-900 dark:text-white">{service.usage.invocations_last_30d.toLocaleString()}</div>
                <div>calls / 30d</div>
              </div>
              {service.usage.cost_last_30d > 0 && (
                <div className="text-right">
                  <div className="font-semibold text-gray-900 dark:text-white">${service.usage.cost_last_30d.toFixed(4)}</div>
                  <div>cost / 30d</div>
                </div>
              )}
            </>
          )}
          <div className="text-right">
            <div className="font-medium">{service.models.length}</div>
            <div>models</div>
          </div>
        </div>

        {expanded ? <ChevronUp className="h-5 w-5 text-gray-400 flex-shrink-0" /> : <ChevronDown className="h-5 w-5 text-gray-400 flex-shrink-0" />}
      </button>

      {/* Expanded details */}
      {expanded && (
        <div className="border-t border-gray-200 dark:border-gray-700 px-5 py-4 space-y-4">
          {/* Description */}
          <p className="text-sm text-gray-600 dark:text-gray-300">{service.description}</p>

          {/* Dependency reason */}
          <div className="flex items-start gap-2 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
            <Info className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" />
            <div>
              <div className="text-xs font-medium text-blue-700 dark:text-blue-300 mb-0.5">Application Dependency</div>
              <div className="text-xs text-blue-600 dark:text-blue-400">{service.dependency_reason}</div>
            </div>
          </div>

          {/* Usage stats grid */}
          {hasUsage && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
                <div className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1"><Hash className="h-3 w-3" />Total Calls</div>
                <div className="text-lg font-bold text-gray-900 dark:text-white">{service.usage.total_invocations.toLocaleString()}</div>
              </div>
              <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
                <div className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1"><TrendingUp className="h-3 w-3" />Last 7 Days</div>
                <div className="text-lg font-bold text-gray-900 dark:text-white">{service.usage.invocations_last_7d.toLocaleString()}</div>
              </div>
              <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
                <div className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1"><DollarSign className="h-3 w-3" />Total Cost</div>
                <div className="text-lg font-bold text-gray-900 dark:text-white">${service.usage.total_cost_usd.toFixed(4)}</div>
              </div>
              <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
                <div className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1"><Clock className="h-3 w-3" />Avg Latency</div>
                <div className="text-lg font-bold text-gray-900 dark:text-white">{service.usage.avg_latency_ms != null ? `${service.usage.avg_latency_ms}ms` : 'N/A'}</div>
              </div>
              {service.usage.success_rate != null && (
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
                  <div className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1"><CheckCircle className="h-3 w-3" />Success Rate</div>
                  <div className="text-lg font-bold text-gray-900 dark:text-white">{(service.usage.success_rate * 100).toFixed(1)}%</div>
                </div>
              )}
              {service.usage.total_tokens > 0 && (
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
                  <div className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1"><Zap className="h-3 w-3" />Total Tokens</div>
                  <div className="text-lg font-bold text-gray-900 dark:text-white">{service.usage.total_tokens.toLocaleString()}</div>
                </div>
              )}
            </div>
          )}

          {/* Models */}
          {service.models.length > 0 && (
            <div>
              <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">Models & Pricing</div>
              <div className="space-y-1.5">
                {service.models.map((m) => <ModelRow key={m.model_id} model={m} />)}
              </div>
            </div>
          )}

          {/* Metering & cost info */}
          {service.metering_notes && (
            <div className="flex items-start gap-2 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
              <DollarSign className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
              <div>
                <div className="text-xs font-medium text-amber-700 dark:text-amber-300 mb-0.5">Metering: {METERING_LABELS[service.metering_type] || service.metering_type}</div>
                <div className="text-xs text-amber-600 dark:text-amber-400">{service.metering_notes}</div>
              </div>
            </div>
          )}

          {/* Environment variables */}
          {service.env_vars.length > 0 && (
            <div>
              <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">Environment Variables</div>
              <div className="flex flex-wrap gap-1.5">
                {service.env_vars.map((v) => (
                  <code key={v} className="text-[10px] px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded font-mono">{v}</code>
                ))}
              </div>
            </div>
          )}

          {/* Files using */}
          {service.files_using.length > 0 && (
            <div>
              <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
                Codebase References ({service.files_using.length} files)
              </div>
              <div className="flex flex-wrap gap-1.5">
                {service.files_using.map((f) => (
                  <code key={f} className="text-[10px] px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded font-mono">{f}</code>
                ))}
              </div>
            </div>
          )}

          {/* Documentation link */}
          {service.documentation_url && (
            <a
              href={service.documentation_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400 hover:underline"
            >
              <ExternalLink className="h-3 w-3" /> API Documentation
            </a>
          )}
        </div>
      )}
    </div>
  );
}

// ── Main Component ───────────────────────────────────────────────────────

export function ExternalServicesCatalog() {
  const { t } = useAppTranslation();
  const tabbed = useTabbedChrome();
  const [catalog, setCatalog] = useState<ExternalServicesCatalogResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [activeDependency, setActiveDependency] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedService, setExpandedService] = useState<string | null>(null);

  const loadCatalog = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchExternalServicesCatalog(
        activeCategory || undefined,
        activeDependency || undefined,
      );
      setCatalog(data);
    } catch (err: any) {
      setError(err?.response?.data?.detail || err.message || 'Failed to load catalog');
    } finally {
      setLoading(false);
    }
  }, [activeCategory, activeDependency]);

  useEffect(() => { loadCatalog(); }, [loadCatalog]);

  // Filter by search
  const filteredServices = catalog?.services.filter((s) => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      s.name.toLowerCase().includes(q) ||
      s.provider.toLowerCase().includes(q) ||
      s.description.toLowerCase().includes(q) ||
      s.models.some((m) => m.display_name.toLowerCase().includes(q) || m.model_id.toLowerCase().includes(q))
    );
  }) || [];

  // Group by category for display
  const categoryGroups: Record<string, ExternalService[]> = {};
  filteredServices.forEach((s) => {
    if (!categoryGroups[s.category]) categoryGroups[s.category] = [];
    categoryGroups[s.category].push(s);
  });

  // Ordered categories
  const orderedCategories = Object.keys(CATEGORY_CONFIG).filter((c) => categoryGroups[c]);

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          {!tabbed && (
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
              <Zap className="h-6 w-6 text-blue-600" />
              {t('external_services.title', 'External Services & API Catalog')}
            </h1>
          )}
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {t('external_services.subtitle', 'All external services, AI providers, and third-party integrations with usage metrics and cost tracking.')}
          </p>
        </div>
        <button
          onClick={loadCatalog}
          disabled={loading}
          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50 transition-colors"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          Refresh
        </button>
      </div>

      {/* Summary cards */}
      {catalog?.summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard
            label="Total Services"
            value={catalog.summary.total_services.toString()}
            subValue={`${Object.keys(catalog.summary.services_by_category).length} categories`}
            icon={Zap}
            color="blue"
          />
          <StatCard
            label="AI Cost (30d)"
            value={`$${catalog.summary.total_ai_cost_30d.toFixed(2)}`}
            subValue="LLM + Embedding + Audio"
            icon={Brain}
            color="purple"
          />
          <StatCard
            label="Total Invocations (30d)"
            value={catalog.summary.total_invocations_30d.toLocaleString()}
            subValue="Tracked AI operations"
            icon={BarChart3}
            color="green"
          />
          <StatCard
            label="Top Cost Service"
            value={catalog.summary.top_cost_services[0]?.service || 'N/A'}
            subValue={catalog.summary.top_cost_services[0] ? `$${catalog.summary.top_cost_services[0].cost_30d.toFixed(4)} / 30d` : ''}
            icon={DollarSign}
            color="amber"
          />
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search services, providers, models..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        <select
          value={activeCategory || ''}
          onChange={(e) => setActiveCategory(e.target.value || null)}
          className="px-3 py-2.5 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300"
        >
          <option value="">All Categories</option>
          {Object.entries(CATEGORY_CONFIG).map(([id, cfg]) => (
            <option key={id} value={id}>{cfg.label}</option>
          ))}
        </select>

        <select
          value={activeDependency || ''}
          onChange={(e) => setActiveDependency(e.target.value || null)}
          className="px-3 py-2.5 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300"
        >
          <option value="">All Dependencies</option>
          <option value="critical">Critical</option>
          <option value="important">Important</option>
          <option value="optional">Optional</option>
        </select>
      </div>

      {/* Error */}
      {error && (
        <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-300 flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 flex-shrink-0" />
          {error}
        </div>
      )}

      {/* Loading */}
      {loading && !catalog && (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
          <span className="ml-3 text-gray-500 dark:text-gray-400">Loading external services catalog...</span>
        </div>
      )}

      {/* Service list grouped by category */}
      {!loading && catalog && (
        <div className="space-y-8">
          {orderedCategories.map((catId) => {
            const catConfig = CATEGORY_CONFIG[catId];
            const CatIcon = catConfig.icon;
            const svcs = categoryGroups[catId];

            return (
              <div key={catId}>
                <div className="flex items-center gap-2 mb-3">
                  <CatIcon className={`h-5 w-5 text-${catConfig.color}-600 dark:text-${catConfig.color}-400`} />
                  <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-200">{catConfig.label}</h2>
                  <span className="text-xs text-gray-400 dark:text-gray-500">({svcs.length})</span>
                </div>
                <div className="space-y-3">
                  {svcs.map((svc) => (
                    <ServiceCard
                      key={svc.service_id}
                      service={svc}
                      expanded={expandedService === svc.service_id}
                      onToggle={() => setExpandedService(expandedService === svc.service_id ? null : svc.service_id)}
                    />
                  ))}
                </div>
              </div>
            );
          })}

          {filteredServices.length === 0 && (
            <div className="text-center py-16 text-gray-400 dark:text-gray-500">
              <Filter className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <div className="text-lg font-medium">No services found</div>
              <div className="text-sm mt-1">Try adjusting your filters or search query.</div>
            </div>
          )}
        </div>
      )}

      {/* Footer: generated timestamp */}
      {catalog?.generated_at && (
        <div className="text-xs text-gray-400 dark:text-gray-500 text-right pt-4 border-t border-gray-200 dark:border-gray-700">
          Catalog generated at {new Date(catalog.generated_at).toLocaleString()}
        </div>
      )}
    </div>
  );
}
